/**
 * CampaignProgress — renders one autonomous-campaign event in the Workflow Checker.
 *
 * The Deputy delivers campaign events through `onCampaignEvent`; Workflow Checker
 * appends each to the campaign runner panel. Two event kinds:
 *   - `decision-record`   → a compact one-line decision (step, action, rationale)
 *   - `campaign-complete` → the terminal status + the rendered markdown narrative
 *
 * Presentational + defensive: tolerates a partial record and never throws, so a
 * malformed or early-terminated stream still renders. Event-type strings match
 * the wire FrameType values; the UI stays decoupled from the deputy module.
 *
 * @module ui/CampaignProgress
 */

import GetAppIcon from '@mui/icons-material/GetApp';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material';
import { Marked } from 'marked';
import sanitizeHtml from 'sanitize-html';
import { useId, useState } from 'react';
import { downloadFile } from '../gibbs/workflow/summaryExport';
import {
    CampaignArtifactKind,
    campaignArtifactLabel,
    campaignStatusLabel,
} from '../gibbs/autonomousCampaign/narrative/campaignArtifacts';

const campaignMarked = new Marked();

const CAMPAIGN_MARKDOWN_TAGS = [
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'p', 'br', 'hr',
    'ul', 'ol', 'li',
    'strong', 'em', 'b', 'i',
    'code', 'pre',
    'blockquote',
    'a',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
];

const CAMPAIGN_MARKDOWN_ATTRIBUTES = {
    a: ['href', 'title', 'target', 'rel'],
};

function escapeHtml(text) {
    return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function coerceNarrativeText(narrative) {
    if (typeof narrative === 'string') return narrative;
    if (narrative == null) return '';
    try {
        const json = JSON.stringify(narrative);
        if (typeof json === 'string') return json;
    } catch {
        // Fall through to String() below.
    }
    return String(narrative);
}

function safelyFormatText(value, formatter) {
    const text = coerceNarrativeText(value);
    if (typeof formatter !== 'function') return text;
    try {
        const formatted = formatter(text);
        return typeof formatted === 'string' ? formatted : text;
    } catch {
        return text;
    }
}

function renderCampaignMarkdown(narrative) {
    const text = coerceNarrativeText(narrative);
    let html;
    try {
        html = campaignMarked.parse(text);
    } catch {
        html = `<p>${escapeHtml(text)}</p>`;
    }
    return sanitizeHtml(html, {
        allowedTags: CAMPAIGN_MARKDOWN_TAGS,
        allowedAttributes: CAMPAIGN_MARKDOWN_ATTRIBUTES,
        allowedSchemes: ['https'],
        disallowedTagsMode: 'completelyDiscard',
        transformTags: {
            a: (tagName, attribs) => ({
                tagName,
                attribs: {
                    ...attribs,
                    target: '_blank',
                    rel: 'noreferrer',
                },
            }),
        },
    });
}

function NarrativeMarkdown({ id, narrative }) {
    return (
        <Box
            id={id}
            className="campaign-narrative"
            sx={{
                fontSize: '0.875rem',
                lineHeight: 1.45,
                overflowX: 'auto',
                overflowWrap: 'anywhere',
                '& > :first-of-type': { mt: 0 },
                '& > :last-child': { mb: 0 },
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                    fontSize: '1rem',
                    lineHeight: 1.25,
                    mt: 1.5,
                    mb: 0.75,
                },
                '& p': { my: 0.75 },
                '& ul, & ol': { my: 0.75, pl: 3 },
                '& li': { mb: 0.35 },
                '& pre': {
                    m: 0,
                    p: 1,
                    overflowX: 'auto',
                    backgroundColor: '#f7f7f7',
                    borderRadius: 0.5,
                },
                '& code': {
                    fontFamily: 'monospace',
                    fontSize: '0.8125rem',
                },
                '& table': {
                    width: '100%',
                    borderCollapse: 'collapse',
                    my: 0.75,
                },
                '& th, & td': {
                    border: '1px solid',
                    borderColor: 'divider',
                    px: 0.75,
                    py: 0.35,
                    textAlign: 'left',
                },
            }}
            dangerouslySetInnerHTML={{ __html: renderCampaignMarkdown(narrative) }}
        />
    );
}

function narrativeFilename(narrative, { maskedAudit = false, kind = 'narrative' } = {}) {
    const text = coerceNarrativeText(narrative);
    const started = text.match(/\*\*Started:\*\*\s*([^\n]+)/);
    const timestamp = started && started[1]
        ? started[1].trim().replace(/[:.]/g, '-')
        : new Date().toISOString().replace(/[:.]/g, '-');
    const qualifier = maskedAudit ? '-masked-audit' : '';
    // A refused campaign is not a narrative and its filename should not claim to
    // be one — someone collecting these should be able to tell them apart.
    return `campaign-${kind}${qualifier}-${timestamp}.md`;
}

function DecisionLine({ record, formatText, showRationale = false }) {
    const r = record || {};
    const step = typeof r.stepIndex === 'number' ? `Step ${r.stepIndex}` : 'Decision';
    const target = r.nextActionId ? ` → ${r.nextActionId}` : '';
    return (
        <Box className="campaign-decision-record">
            <Typography component="div" sx={{ fontSize: 'small' }}>
                <strong>{`${step} — ${r.decision || '(no decision)'}${target}`}</strong>
                {r.actionId ? (
                    <span style={{ color: 'gray' }}>{` · ${r.actionId}`}</span>
                ) : null}
            </Typography>
            {/*
              * The rationale is a paragraph per step. It is the narrative's job —
              * that document is what a reader is meant to take away — and shown
              * here for every step it pushes the step list itself below the fold.
              * Off by default, one toggle to bring it back.
              */}
            {r.rationale && showRationale ? (
                <Typography component="div" sx={{ fontSize: 'small', color: 'text.secondary' }}>
                    {safelyFormatText(r.rationale, formatText)}
                </Typography>
            ) : null}
        </Box>
    );
}

function CompletionBlock({
    status, narrative, formatMarkdown, label, kind = 'narrative',
}) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const narrativeId = useId();
    const narrativeText = coerceNarrativeText(narrative);
    const displayNarrativeText = safelyFormatText(narrativeText, formatMarkdown);
    const hasNarrative = narrativeText.length > 0;
    const downloadNarrative = () => downloadFile(
        displayNarrativeText,
        narrativeFilename(narrativeText, { kind }),
        'text/markdown',
    );
    const downloadMaskedAudit = () => downloadFile(
        narrativeText,
        narrativeFilename(narrativeText, { maskedAudit: true, kind }),
        'text/markdown',
    );

    return (
        <Box className="campaign-complete">
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                mb: hasNarrative ? 0.5 : 0,
            }}
            >
                <Typography component="div" sx={{ fontWeight: 'bold' }}>
                    {label || `Campaign ${campaignStatusLabel(status)?.toLowerCase() || status || '(unknown)'}`}
                </Typography>
                {hasNarrative ? (
                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<GetAppIcon fontSize="small" />}
                            onClick={downloadNarrative}
                            data-testid="campaign-download-narrative"
                        >
                            Download
                        </Button>
                        <Button
                            size="small"
                            variant="text"
                            onClick={() => setDialogOpen(true)}
                            aria-haspopup="dialog"
                            aria-controls={dialogOpen ? narrativeId : undefined}
                            data-testid="campaign-toggle-narrative"
                        >
                            Open
                        </Button>
                    </Box>
                ) : null}
            </Box>
            {hasNarrative ? (
                <Dialog
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    fullWidth
                    maxWidth="md"
                    scroll="paper"
                    aria-labelledby={`${narrativeId}-title`}
                    data-testid="campaign-narrative-dialog"
                >
                    <DialogTitle id={`${narrativeId}-title`}>
                        Campaign narrative
                    </DialogTitle>
                    <DialogContent dividers>
                        <NarrativeMarkdown id={narrativeId} narrative={displayNarrativeText} />
                    </DialogContent>
                    <DialogActions>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<GetAppIcon fontSize="small" />}
                            onClick={downloadNarrative}
                            data-testid="campaign-dialog-download-narrative"
                        >
                            Download
                        </Button>
                        <Button
                            size="small"
                            variant="text"
                            startIcon={<GetAppIcon fontSize="small" />}
                            onClick={downloadMaskedAudit}
                            data-testid="campaign-dialog-download-masked-audit"
                        >
                            Download masked audit
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={() => setDialogOpen(false)}
                            data-testid="campaign-close-narrative"
                        >
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            ) : null}
        </Box>
    );
}

export default function CampaignProgress({
    event,
    formatText = (text) => text,
    formatMarkdown = (text) => text,
    showRationale = false,
}) {
    const e = event || {};
    if (e.type === 'campaign-complete') {
        return (
            <CompletionBlock
                status={e.status}
                narrative={e.narrative}
                formatMarkdown={formatMarkdown}
            />
        );
    }
    if (e.type === 'decision-record') {
        return (
            <DecisionLine
                record={e.record}
                formatText={formatText}
                showRationale={showRationale}
            />
        );
    }
    return null;
}

/**
 * Every narrative produced in this session, newest first.
 *
 * Her ask, 2026-07-28: *"any downloads from the session, I think, should still be
 * accessible"* — with *"you don't need to see all of these steps and all their
 * details."* So this is the artifact surface, deliberately separate from the live
 * progress stream: closing the panel, starting a second campaign, or reloading the
 * page resets that stream, and until now took the finished run's only download
 * button with it.
 *
 * Each entry reuses CompletionBlock, so See / Download / Download masked audit
 * behave identically to a just-finished run — including keeping the decoded and
 * masked-audit copies distinct.
 *
 * @param {Object} props
 * @param {{id: string, status: string, narrative: string, label: string}[]} props.artifacts
 * @param {function} [props.formatMarkdown] one formatter for every entry
 * @param {function} [props.markdownFormatterFor] `(artifact) => (text) => string`.
 *   Preferred: a document carries the alias bindings it was written with, and a
 *   session can hold documents from more than one workspace — a restored file's,
 *   or an earlier one loaded in the same session. A single live formatter would
 *   decode all of them with the latest workspace's map.
 */
export function SessionNarrativeList({
    artifacts = [], formatMarkdown = (text) => text, markdownFormatterFor = null, onDiscard,
}) {
    // Two-step rather than a modal: the panel is narrow, and one stray click
    // should not clear a list of results someone may still want to download.
    const [confirmingDiscard, setConfirmingDiscard] = useState(false);
    const withNarrative = artifacts.filter((entry) => coerceNarrativeText(entry?.narrative));
    if (withNarrative.length === 0) return null;
    return (
        <Box
            sx={{
                mt: 1, p: 0.75, border: '1px solid', borderColor: 'divider', borderRadius: 1,
            }}
            data-testid="campaign-session-narratives"
        >
            <Box sx={{
                display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1,
            }}
            >
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', fontWeight: 'bold', mb: 0.5 }}
                >
                    {withNarrative.length === 1
                        ? 'Previous narrative'
                        : `Previous narratives (${withNarrative.length})`}
                </Typography>
                {onDiscard ? (
                    <Box sx={{
                        display: 'flex', alignItems: 'baseline', gap: 0.5, flexShrink: 0,
                    }}
                    >
                        {confirmingDiscard ? (
                            <Button
                                size="small"
                                variant="text"
                                onClick={() => setConfirmingDiscard(false)}
                                data-testid="campaign-discard-narratives-cancel"
                            >
                                Keep
                            </Button>
                        ) : null}
                        <Button
                            size="small"
                            variant="text"
                            color="error"
                            onClick={() => {
                                if (!confirmingDiscard) {
                                    setConfirmingDiscard(true);
                                    return;
                                }
                                setConfirmingDiscard(false);
                                onDiscard();
                            }}
                            data-testid="campaign-discard-narratives"
                        >
                            {confirmingDiscard ? 'Confirm discard' : 'Discard'}
                        </Button>
                    </Box>
                ) : null}
            </Box>
            {withNarrative.map((entry) => (
                <CompletionBlock
                    key={entry.id}
                    label={campaignArtifactLabel(entry)}
                    status={entry.status}
                    narrative={entry.narrative}
                    kind={entry.kind === CampaignArtifactKind.Refusal ? 'refusal' : 'narrative'}
                    formatMarkdown={
                        markdownFormatterFor ? markdownFormatterFor(entry) : formatMarkdown
                    }
                />
            ))}
        </Box>
    );
}

/**
 * The downloadable record for a campaign that was refused before it ran.
 *
 * Her 07-28 ask: error messages have to be exportable too. A refusal produces no
 * `campaign-complete` frame, so `CompletionBlock` had nothing to render and the
 * reviewer was left with an on-screen message and no artifact.
 *
 * Reuses CompletionBlock so Download, Download masked audit and Open behave
 * identically to a finished run — the decoded and masked copies stay distinct here
 * too — while the filename and heading say plainly that nothing ran.
 *
 * @param {Object} props
 * @param {string} props.record markdown from `renderCampaignRefusalRecord`
 * @param {function} [props.formatMarkdown]
 */
export function CampaignRefusalRecord({ record, formatMarkdown = (text) => text }) {
    if (!coerceNarrativeText(record)) return null;
    return (
        <Box
            sx={{
                mt: 1, p: 0.75, border: '1px solid', borderColor: 'divider', borderRadius: 1,
            }}
            data-testid="campaign-refusal-record"
        >
            <CompletionBlock
                label="Refusal record — nothing ran"
                status="refused"
                kind="refusal"
                narrative={record}
                formatMarkdown={formatMarkdown}
            />
        </Box>
    );
}
